import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { commsApi } from './phase567Api';
import usePolling from './usePolling';
import { DEFAULT_FAVICON } from '../branding';

// Ticket 154: one 15 s poll of the inbox summary, shared by the nav badge and
// the Inbox screens. `live` is the server's view of the fast GHL pull
// ({state: 'off' | 'ok' | 'paused', last_ok_at}).
export const LIVE_POLL_MS = 15000;
// Ticket 154 P3: a hidden tab polls this slowly, and only while the user has
// browser notifications switched on.
export const LIVE_HIDDEN_POLL_MS = 30000;
const SEEN_CAP = 200;

const INERT = {
  unreadContacts: 0,
  live: { state: 'off' },
  refresh() {},
  notify: { enabled: false, permission: 'default', busy: false },
  setNotifyEnabled() {},
};

const readPermission = () => (
  typeof window !== 'undefined' && window.Notification ? window.Notification.permission : 'unsupported'
);
const LiveInboxContext = createContext(INERT);

// `enabled` stays false until a portal user is signed in (no 401 polling).
export function LiveInboxProvider({ children, enabled = true }) {
  const [summary, setSummary] = useState({ unreadContacts: 0, live: { state: 'off' } });
  const navigate = useNavigate();
  const [notify, setNotify] = useState({ enabled: false, permission: readPermission(), busy: false });
  // Server `now` from the last poll: the next `since`, so alerts never depend on the client clock.
  const sinceRef = useRef(null);
  const notifyOnRef = useRef(false);
  const seenRef = useRef(new Set());

  const fireAlerts = useCallback((alerts) => {
    const seen = seenRef.current;
    const granted = readPermission() === 'granted';
    alerts.forEach((row) => {
      if (seen.has(row.message_id)) return;
      seen.add(row.message_id);
      if (seen.size > SEEN_CAP) seen.delete(seen.values().next().value);
      if (!granted) return;
      const viewing = document.visibilityState !== 'hidden'
        && window.location.pathname === '/portal/inbox'
        && new URLSearchParams(window.location.search).get('c') === row.contact_id;
      if (viewing) return;
      try {
        const n = new window.Notification(`New text from ${row.name}`, { tag: row.message_id, icon: DEFAULT_FAVICON });
        n.onclick = () => {
          window.focus();
          navigate(`/portal/inbox?c=${encodeURIComponent(row.contact_id)}`);
          n.close();
        };
      } catch { /* some mobile browsers throw on the constructor */ }
    });
  }, [navigate]);

  // Only the newest request may apply: an older answer landing late would
  // bring back a badge a mark-read refresh just cleared.
  const seq = useRef(0);

  const refresh = useCallback(() => {
    const mine = ++seq.current;
    const params = notifyOnRef.current && sinceRef.current ? { since: sinceRef.current } : {};
    return commsApi.inboxSummary(params)
    .then((d) => {
      if (mine !== seq.current) return;
      if (d?.now) sinceRef.current = d.now;
      const enabled = !!d?.notify?.enabled;
      notifyOnRef.current = enabled;
      setNotify((prev) => {
        const permission = readPermission();
        return prev.enabled === enabled && prev.permission === permission
          ? prev : { ...prev, enabled, permission };
      });
      if (enabled && Array.isArray(d?.alerts)) fireAlerts(d.alerts);
      const next = { unreadContacts: d?.unread_contacts || 0, live: d?.live || { state: 'off' } };
      // Same values -> keep the old object so consumers do not re-render.
      // Paused always updates so its "N minutes ago" keeps counting.
      setSummary((prev) => (
        next.live.state !== 'paused'
        && prev.unreadContacts === next.unreadContacts
        && prev.live.state === next.live.state
        && prev.live.last_ok_at === next.live.last_ok_at ? prev : next
      ));
    })
    // A failed request keeps the last value.
    .catch(() => {});
  }, [fireAlerts]);

  const setNotifyEnabled = useCallback(async (on) => {
    if (on) {
      if (readPermission() === 'unsupported') {
        setNotify((prev) => ({ ...prev, permission: 'unsupported' }));
        return;
      }
      const permission = await window.Notification.requestPermission();
      setNotify((prev) => ({ ...prev, permission }));
      if (permission !== 'granted') return;
    }
    setNotify((prev) => ({ ...prev, busy: true }));
    try {
      await commsApi.setNotifyPrefs({ notify_client_texts: !!on });
      // A poll answered before the PUT must not land afterwards and undo it.
      seq.current += 1;
      notifyOnRef.current = !!on;
      setNotify((prev) => ({ ...prev, enabled: !!on, busy: false }));
    } catch {
      // Keep the previous value on failure.
      setNotify((prev) => ({ ...prev, busy: false }));
    }
  }, []);

  usePolling(refresh, LIVE_POLL_MS, { enabled, hiddenMs: notify.enabled ? LIVE_HIDDEN_POLL_MS : 0 });

  const value = useMemo(
    () => ({ ...summary, refresh, notify, setNotifyEnabled }),
    [summary, refresh, notify, setNotifyEnabled],
  );
  return <LiveInboxContext.Provider value={value}>{children}</LiveInboxContext.Provider>;
}

export function useLiveInbox() {
  return useContext(LiveInboxContext);
}
